import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Prompt } from 'react-router';
import styled from 'styled-components';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { AreaChart, BarChart, ColumnChart, LineChart, NumberChart, SplineChart } from 'ui/icons';
import { Skeleton } from 'ui/skeleton';
import Chart from 'core/chart';
import { InvalidIdMessage } from 'modules/alerts/invalidIdMessage';
import { selectUserProject } from 'core/userContext';
import { fetchMetricsRequest } from 'modules/metrics';
import { DeleteReportModal } from 'modules/deleteReportModal';
import { ReportControls } from './ReportControls';
import { ReportFilters } from './ReportFilters';
import ReportDetails from './ReportDetails';
import {
  fetchReportRequest,
  updateChartType,
  resetReport,
  initializeSelectedTypeIds,
  resetReportDataViews,
} from '../actions';
import {
  getActiveReport,
  selectReportDataSourceModal,
  selectReportChartState,
  selectFormattedHighLevelMetrics,
  getReportHasUnsavedChanges,
} from '../selectors';

const getIcon = (type) => `${type.charAt(0).toUpperCase()}${type.substr(1).toLowerCase()}Chart`;

const CHART_MAP = {
  AreaChart: AreaChart,
  BarChart: BarChart,
  LineChart: LineChart,
  ColumnChart: ColumnChart,
  NumberChart: NumberChart,
  SplineChart: SplineChart,
};

const Report = () => {
  const report = useSelector(getActiveReport);
  const chartConfig = useSelector(selectReportChartState) || {};
  const highLevelMetrics = useSelector(selectFormattedHighLevelMetrics);
  const userProject = useSelector(selectUserProject);
  const { loadingData } = useSelector(selectReportDataSourceModal);
  const params = useParams();
  const reportId = report && report.id;
  const { selectChartType, chartType, activeView, config } = chartConfig || {};
  const ChartType = Chart.getChartType(chartType);
  const ChartIcon = CHART_MAP[getIcon(chartType)] || LineChart;
  const unsavedChanges = useSelector(getReportHasUnsavedChanges);

  const StyledChartIcon = styled(ChartIcon)`
    padding: 5px;
  `;
  const StyledChartType = styled(ChartType)`
    width: 100%;
  `;

  const dispatch = useDispatch();

  useEffect(() => {
    if (params.id) {
      if (highLevelMetrics.length === 0) {
        dispatch(fetchMetricsRequest());
      }

      dispatch(fetchReportRequest(params.id));
      dispatch(initializeSelectedTypeIds(report.componentType, report.componentIds));
    }

    return () => {
      dispatch(resetReport());
      dispatch(resetReportDataViews());
    };
  }, []);

  useEffect(() => {
    if (!unsavedChanges) {
      return;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();

      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  if (loadingData) {
    return <Skeleton data-test="reportLoadingSkeleton" height={500} />;
  }
  return reportId ? (
    <SemanticUI.Container fluid>
      <Prompt
        when={unsavedChanges}
        message={() => 'You have unsaved changes are you sure you want to leave?'}
      />
      <DeleteReportModal
        deleteReportData={{
          reportName: report.name,
          reportId,
        }}
        redirectOnComplete={true}
      />
      <ReportControls />
      <ReportFilters />
      <SemanticUI.Segment attached>
        <SemanticUI.Grid columns={2}>
          <SemanticUI.Grid.Column textAlign="left">
            <SemanticUI.Header as="h3" content="Metrics" />
          </SemanticUI.Grid.Column>
          <SemanticUI.Grid.Column textAlign="right">
            <SemanticUI.Dropdown
              button
              icon={<StyledChartIcon width={16} height={16} className="ui icon" />}
              className="icon"
              labeled
              options={selectChartType}
              onChange={(e, d) => dispatch(updateChartType(reportId, activeView, d.value))}
              value={chartType}
            />
          </SemanticUI.Grid.Column>
        </SemanticUI.Grid>
        <StyledChartType
          data={config.data}
          options={{
            ...config,
            loading: !report,
          }}
        />
      </SemanticUI.Segment>
      <ReportDetails />
    </SemanticUI.Container>
  ) : (
    <InvalidIdMessage
      data-test="invalidIdMessage"
      idType="ReportId"
      id={params.id}
      project={userProject}
    />
  );
};
export default Report;
